import type { IValidationErrorItem } from 'models';
import type { FlatTunnelErrors } from 'app/slices/ahuValidationSlice';

/**
 * Phase 6e1 — smart-resolve Helper port (§8.1.1,
 * error.log.resolution.helper.js — catalog/option labels COPIED VERBATIM,
 * lines 23-299 of the source; parity 152). Pure functions — table-driven
 * testable per R13.
 */

export interface ResolveOption {
  label: string;
  value: string;
}

interface SelectEntry {
  options: ResolveOption[];
  type: 'section' | 'tunnel' | 'accessory';
}

/** `selects(item)` — trans_item derivation + the 18-key catalog (verbatim). */
export function buildSelects(item: string | undefined): Record<string, SelectEntry> {
  const transItem = item?.split
    ? item
        .split('\n:')
        .map((line) => line.split(':')[0].trim())
        .join('')
    : item ?? '';
  const t = transItem;
  return {
    error_min_clr_ups: {
      options: [
        {
          label: `Add an Plenum Section upstream, with the minimum required length`,
          value: 'add_plenum',
        },
      ],
      type: 'section',
    },
    error_min_clr_ups_inc: {
      options: [{ label: 'Increase the section length', value: 'inc_length' }],
      type: 'section',
    },
    error_min_clr_ups_inc_ups: {
      options: [
        { label: 'Increase the length of the upstream section', value: 'inc_ups_length' },
      ],
      type: 'section',
    },
    error_min_clr_downs: {
      options: [{ label: 'Increase the section length', value: 'inc_length' }],
      type: 'section',
    },
    does_not_fit_in_width: {
      options: [{ label: 'Increase tunnel width', value: 'inc_tunnel_width' }],
      type: 'tunnel',
    },
    does_not_fit_in_height: {
      options: [{ label: 'Increase tunnel height', value: 'inc_tunnel_height' }],
      type: 'tunnel',
    },
    does_not_fit_in_length: {
      options: [{ label: 'Increase the section length', value: 'inc_length' }],
      type: 'section',
    },
    error_min_clr_down: {
      options: [{ label: 'Increase the section length', value: 'inc_length' }],
      type: 'section',
    },
    error_min_clr_flr_reduce: {
      options: [
        {
          label: `Move the ${t} up, reduce the ${t} height as allowed and increase the tunnel height, if necessary`,
          value: 'move_top_reduce_inc_tunnel_height',
        },
        {
          label: `Move the ${t} up and increase the tunnel height, if necessary`,
          value: 'move_top_inc_tunnel_height',
        },
        {
          label: `Move the ${t} up and reduce the ${t} height as allowed, if necessary`,
          value: 'move_top_reduce',
        },
        { label: `Move the ${t} up`, value: 'move_top' },
      ],
      type: 'accessory',
    },
    error_min_clr_rf_reduce: {
      options: [
        {
          label: `Move the ${t} down, reduce the ${t} height as allowed and increase the tunnel height, if necessary`,
          value: 'move_btm_reduce_inc_tunnel_height',
        },
        {
          label: `Move the ${t} down and reduce the ${t} height as allowed, if necessary`,
          value: 'move_btm_reduce',
        },
        {
          label: `Move the ${t} down and increase the tunnel height, if necessary`,
          value: 'move_btm_inc_tunnel_height',
        },
        { label: `Move the ${t} down`, value: 'move_btm' },
      ],
      type: 'accessory',
    },
    error_min_clr_flr: {
      options: [
        {
          label: `Move the ${t} up and increase the tunnel height, if necessary`,
          value: 'move_top_inc_tunnel_height',
        },
        { label: `Move the ${t} up`, value: 'move_top' },
      ],
      type: 'accessory',
    },
    error_min_clr_rf: {
      options: [
        {
          label: `Move the ${t} down and increase the tunnel height, if necessary`,
          value: 'move_btm_inc_tunnel_height',
        },
        { label: `Move the ${t} down`, value: 'move_btm' },
      ],
      type: 'accessory',
    },
    error_min_clr_downs_accessory: {
      options: [
        {
          label: `Move the ${t} upstream and increase the section length if necessary`,
          value: 'move_ups_inc_section_length',
        },
        { label: 'Increase the section length', value: 'inc_length' },
      ],
      type: 'accessory',
    },
    error_min_clr_ups_accessory: {
      options: [
        {
          label: `Move the ${t} downstream and increase the section length if necessary`,
          value: 'move_down_inc_section_length',
        },
        { label: `Move the ${t} downstream`, value: 'move_down' },
      ],
      type: 'accessory',
    },
    error_min_clr_right_wall_reduce: {
      options: [
        {
          label: `Move the ${t} to the left, reduce the ${t} width as possible and increase the tunnel width, if necessary`,
          value: 'move_left_reduce_inc_tunnel_width',
        },
        {
          label: `Move the ${t} to the left and reduce the ${t} width as allowed, if necessary`,
          value: 'move_left_reduce',
        },
        {
          label: `Move the ${t} to the left and increase the tunnel width if necessary`,
          value: 'move_left_inc_tunnel_width',
        },
        { label: `Move the ${t} to the left`, value: 'move_left' },
      ],
      type: 'accessory',
    },
    error_min_clr_left_wall_reduce: {
      options: [
        {
          label: `Move the ${t} to the right, reduce the ${t} width as possible and increase the tunnel width, if necessary`,
          value: 'move_right_reduce_inc_tunnel_width',
        },
        {
          label: `Move the ${t} to the right and reduce the ${t} width as allowed, if necessary`,
          value: 'move_right_reduce',
        },
        {
          label: `Move the ${t} to the right and increase the tunnel width if necessary`,
          value: 'move_right_inc_tunnel_width',
        },
        { label: `Move the ${t} to the right`, value: 'move_right' },
      ],
      type: 'accessory',
    },
    error_min_clr_right_wall_accessory: {
      options: [
        {
          label: `Move the ${t} to the left and increase the tunnel width if necessary`,
          value: 'move_left_inc_tunnel_width',
        },
        { label: `Move the ${t} to the left`, value: 'move_left' },
      ],
      type: 'accessory',
    },
    error_min_clr_left_wall_accessory: {
      options: [
        {
          label: `Move the ${t} to the right and increase the tunnel width if necessary`,
          value: 'move_right_inc_tunnel_width',
        },
        { label: `Move the ${t} to the right`, value: 'move_right' },
      ],
      type: 'accessory',
    },
    error_min_clr_downs_reduce: {
      options: [
        {
          label: `Move the ${t} upstream, reduce the ${t} width as possible and increase the section length, if necessary`,
          value: 'move_ups_reduce_inc_section_length',
        },
        {
          label: `Move the ${t} upstream and reduce the ${t} width as possible, if necessary`,
          value: 'move_ups_reduce',
        },
        {
          label: `Move the ${t} upstream and increase the section length if necessary`,
          value: 'move_ups_inc_section_length',
        },
        { label: `Increase section length`, value: 'inc_length' },
      ],
      type: 'accessory',
    },
    error_min_clr_ups_reduce: {
      options: [
        {
          label: `Move the ${t} downstream, reduce the ${t} width as possible and increase the section length, if necessary`,
          value: 'move_down_reduce_inc_section_length',
        },
        {
          label: `Move the ${t} downstream and reduce the ${t} width as allowed, if necessary`,
          value: 'move_down_reduce',
        },
        {
          label: `Move the ${t} downstream and increase the section length if necessary`,
          value: 'move_down_inc_section_length',
        },
        { label: `Move the ${t} downstream`, value: 'move_down' },
      ],
      type: 'accessory',
    },
  };
}

/** Paired-error suppression map (verbatim). */
export const CONNECT_ERROR: Record<string, string> = {
  error_min_clr_rf: 'error_min_clr_flr',
  error_min_clr_right_wall_accessory: 'error_min_clr_left_wall_accessory',
  error_min_clr_right_wall: 'error_min_clr_left_wall',
  error_min_clr_downs: 'error_min_clr_ups',
  error_min_clr_ups_accessory: 'error_min_clr_downs_accessory',
  error_min_clr_downs_accessory: 'error_min_clr_ups_accessory',
  error_min_clr_flr: 'error_min_clr_rf',
  error_min_clr_left_wall: 'error_min_clr_right_wall',
  error_min_clr_left_wall_accessory: 'error_min_clr_right_wall_accessory',
  error_min_clr_ups: 'error_min_clr_downs',
  error_min_clr_flr_reduce: 'error_min_clr_rf_reduce',
  error_min_clr_rf_reduce: 'error_min_clr_flr_reduce',
  error_min_clr_left_wall_reduce: 'error_min_clr_right_wall_reduce',
  error_min_clr_right_wall_reduce: 'error_min_clr_left_wall_reduce',
};

/** Accessory error renaming (getErrorName — verbatim). */
export function getErrorName(object: string | undefined, error: string | undefined): string {
  if (
    (object === 'access_door' || object === 'opening' || object === 'hood') &&
    (error === 'error_min_clr_flr' ||
      error === 'error_min_clr_rf' ||
      error === 'error_min_clr_downs' ||
      error === 'error_min_clr_ups' ||
      error === 'error_min_clr_right_wall' ||
      error === 'error_min_clr_left_wall')
  ) {
    return `${error}_reduce`;
  }
  if (
    error === 'error_min_clr_ups' ||
    error === 'error_min_clr_downs' ||
    error === 'error_min_clr_right_wall' ||
    error === 'error_min_clr_left_wall'
  ) {
    return `${error}_accessory`;
  }
  return error ?? '';
}

/** Friendly fallback texts (getErrorText — verbatim). */
export function getErrorText(
  error: string,
  message: string | null,
  item: string,
  key?: string
): string {
  if (error === 'apd_too_high') {
    return `Increase the size of the ${item} or decrease the airflow`;
  }
  if (error === 'ahu_too_long') {
    return `Decrease the length of one or mutiple sections`;
  }
  if (error === 'fv_too_high') {
    const name = key === 'components' ? 'component' : 'accessory';
    return `Increase the size or decrease the airflow of the ${name}`;
  }
  if (error === 'fv_too_low') {
    const name = key === 'components' ? 'component' : 'accessory';
    return `Decrease the size or increase the airflow of the ${name}`;
  }
  if (error === 'error_min_clr_left_wall' || error === 'error_min_clr_right_wall') {
    return 'Error will be resolved through another error';
  }
  switch (message) {
    case 'Accessories interfere with each other':
      return 'Two accessories are too close to each other. Move one or both away';
    case 'Rating & Selection Incomplete!':
    case 'Invalid selection! Complete all fields.':
    case 'Invalid selection! Complete all fields. ':
      return `Select the ${item}`;
    case 'Mounting Incomplete!':
      return 'Mount the accessory';
    default:
      return '';
  }
}

export interface ResolveListItem extends IValidationErrorItem {
  error_type: 'global' | 'components' | 'accessories' | 'dmounts';
  section_id?: number;
  tunnel_id?: number;
  key?: string;
  accessory_id?: number;
  unique_id?: string;
  def_error?: string;
  extra_value?: string;
  text?: string;
  resovable?: boolean;
  options?: ResolveOption[];
  only_one_option?: boolean;
  ups_error?: string;
}

let uniqueCounter = 0;
function generateUniqueId(prefix = 'error'): string {
  uniqueCounter += 1;
  return `${prefix}-${Date.now().toString(36)}-${uniqueCounter.toString(36)}`;
}

const NON_RESOLVABLE = [
  'fv_too_high',
  'fv_too_low',
  'ahu_too_long',
  'apd_too_high',
  'd_mount_too_close_to_downs_base',
  'error_min_clr_right_wall',
  'error_min_clr_left_wall',
];

export interface UpsSectionContext {
  /** Re-typing of error_min_clr_ups per upstream-section analysis (§8.1.1). */
  checkUpsSectionType: (
    item: ResolveListItem,
    sectionId: number | undefined,
    tunnelId: number | undefined
  ) => string;
}

function getSelectOptions(
  e: ResolveListItem,
  ctx: UpsSectionContext,
  extra: { section_id?: number; tunnel_id?: number; key?: string } = {},
  transError?: string
): Partial<ResolveListItem> {
  let error = transError || e.error || '';
  const resovable = !!error && !NON_RESOLVABLE.includes(error);
  if (error === 'error_min_clr_ups') {
    error = ctx.checkUpsSectionType(e, extra.section_id, extra.tunnel_id);
  }
  const options = buildSelects(e.item)[error]?.options;
  const text = getErrorText(error, e.message, e.item, extra.key) || e.text || '';
  return {
    resovable,
    options: options || [],
    only_one_option: (options?.length || 0) < 2,
    text,
  };
}

function checkIsExist(
  items: ResolveListItem[],
  error: string | undefined,
  id: unknown
): ResolveListItem | undefined {
  return items.find((a) => (a.error === error || a.def_error === error) && id === a.id);
}

/**
 * transformErrors (Control port): flattens global/components/accessories/
 * dmounts with the accessory paired-error suppression + renames (verbatim
 * branch order).
 */
export function transformErrors(
  errors: FlatTunnelErrors[],
  ctx: UpsSectionContext
): { list: ResolveListItem[]; count: { generate: number; do_not_generate: number } } {
  const list: ResolveListItem[] = [];
  const count = { generate: 0, do_not_generate: 0 };

  const pushCounted = (item: ResolveListItem) => {
    if (item.message) {
      list.push(item);
      if (item.type === 'generate' || item.type === 'do_not_generate') count[item.type] += 1;
    }
  };

  errors.forEach((error) => {
    (error.global ?? []).forEach((g) => {
      const item: ResolveListItem = { ...g, error_type: 'global' };
      if (item.message) Object.assign(item, getSelectOptions(item, ctx));
      pushCounted(item);
    });
    error.sections.forEach((section) => {
      const extra = { section_id: section.id, tunnel_id: error.tunnel_id };
      // ORDER CONTRACT: accessories → components → dmounts (legacy Control
      // typeHandlers iteration + error.log.tmpl — the resolve panel mirrors
      // the error list 1:1 BY INDEX, §8.1.1).
      // accessories (paired-error suppression web — verbatim).
      const accItems: ResolveListItem[] = (section.accessories ?? []).map((a) => ({
        ...a,
        error_type: 'accessories' as const,
      }));
      accItems.forEach((e) => {
        if (
          e.error === 'error_min_clr_flr' ||
          e.error === 'error_min_clr_right_wall' ||
          e.error === 'error_min_clr_right_wall_accessory' ||
          e.error === 'error_min_clr_downs'
        ) {
          const finded = checkIsExist(accItems, CONNECT_ERROR[e.error ?? ''], e.id);
          if (finded) {
            if (!finded.unique_id) {
              const uniqueId = generateUniqueId();
              finded.unique_id = uniqueId;
              e.unique_id = uniqueId;
            }
            e.extra_value = finded.value;
          }
        }
        if (
          e.error === 'error_min_clr_rf' ||
          e.error === 'error_min_clr_ups' ||
          e.error === 'error_min_clr_left_wall' ||
          e.error === 'error_min_clr_left_wall_accessory'
        ) {
          const finded = checkIsExist(accItems, CONNECT_ERROR[e.error ?? ''], e.id);
          if (finded) {
            if (!finded.unique_id) {
              const uniqueId = generateUniqueId();
              finded.unique_id = uniqueId;
              e.unique_id = uniqueId;
            }
            e.def_error = e.error;
            e.error = '';
            e.text = 'Error will be resolved through another error';
          }
        }
        const errorName = e.message ? getErrorName(e.object, e.error) : '';
        const item: ResolveListItem = {
          ...e,
          error: errorName,
          error_type: 'accessories',
          accessory_id: Number(e.id),
          ...extra,
          key: 'accessories',
        };
        if (item.message)
          Object.assign(
            item,
            getSelectOptions(item, ctx, { ...extra, key: 'accessories' }, errorName)
          );
        pushCounted(item);
      });
      // components
      (section.components ?? []).forEach((c) => {
        const item: ResolveListItem = {
          ...c,
          error_type: 'components',
          ...extra,
          key: 'components',
        };
        if (item.message)
          Object.assign(item, getSelectOptions(item, ctx, { ...extra, key: 'components' }));
        pushCounted(item);
      });
      // dmounts
      (section.dmounts ?? []).forEach((d) => {
        const item: ResolveListItem = {
          ...d,
          error_type: 'dmounts',
          ...extra,
          key: 'dmounts',
        };
        if (item.message)
          Object.assign(item, getSelectOptions(item, ctx, { ...extra, key: 'dmounts' }));
        pushCounted(item);
      });
    });
  });
  return { list, count };
}

/** roundBasedOnArguments — round UP to the next increment (verbatim). */
export function roundBasedOnArguments(first: number, second: number): number {
  if (!second) return first;
  if (first % second) return first - (first % second) + second;
  return first;
}

/** calculateClearances (verbatim plane→wall mapping + max merges). */
export function calculateClearances(
  clearances: Record<string, number> = {},
  mountingPlaneId: number | null | undefined
): {
  top: number;
  btm: number;
  left_right: number;
  ups_down: number;
  ups: number;
  downs: number;
} {
  const wall = (
    {
      2: 'rf',
      3: 'end_wall',
      4: 'end_wall',
      5: 'side_wall',
      6: 'side_wall',
      1: 'flr',
    } as Record<number, string>
  )[mountingPlaneId ?? 0];
  return {
    top: Math.max(
      clearances[`min_clear_if_on_${wall}_from_top`] || 0,
      clearances[`min_clear_if_on_${wall}_from_rf`] || 0
    ),
    btm: Math.max(
      clearances[`min_clear_if_on_${wall}_from_flr`] || 0,
      clearances[`min_clear_if_on_${wall}_from_btm`] || 0
    ),
    left_right: Math.max(
      clearances[`min_clear_if_on_${wall}_from_sides`] || 0,
      clearances[`min_clear_if_on_${wall}_from_side_walls`] || 0
    ),
    ups_down: Math.max(
      clearances[`min_clear_if_on_${wall}_from__section_ups_downs`] || 0,
      clearances[`min_clear_if_on_${wall}_from_sides`] || 0
    ),
    ups: Math.max(
      clearances[`min_clear_if_on_${wall}_from_ups`] || 0,
      clearances[`min_clear_if_on_${wall}_from_section_ups_downs`] || 0,
      clearances[`min_clear_if_on_${wall}_from_sides`] || 0
    ),
    downs: Math.max(
      clearances[`min_clear_if_on_${wall}_from_downs`] || 0,
      clearances[`min_clear_if_on_${wall}_from_section_ups_downs`] || 0,
      clearances[`min_clear_if_on_${wall}_from_sides`] || 0
    ),
  };
}

/** calculateOverlapAndChange (verbatim). */
export function calculateOverlapAndChange(
  newVal: number,
  positionVal: number,
  distanceToBoundary: number,
  clearance: number
): number {
  const overlap = newVal - positionVal;
  const remainingDistance = distanceToBoundary - overlap - clearance;
  return remainingDistance < 0 ? -remainingDistance : 0;
}

/** adjustAccessoryDimension (verbatim). */
export function adjustAccessoryDimension(
  currentSize: number,
  requiredChange: number,
  minSize: number
): { newSize: number; extraChange: number } {
  let extraChange = 0;
  let newSize = currentSize;
  if (requiredChange > 0) {
    const reduced = Math.floor(currentSize - requiredChange);
    if (reduced > minSize) {
      newSize = reduced;
    } else {
      extraChange = requiredChange - (currentSize - minSize);
      newSize = minSize;
    }
  }
  return { newSize, extraChange };
}
